import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Plus, Trash2 } from 'lucide-react';
import { QuestionType, CreateOptionDto, CreateSubQuestionDto } from '../../../api/questions';
import { Subject, Topic } from '../../../api/subjects';
import { QuestionFormData } from '../types';

interface QuestionDialogProps {
  isDark: boolean;
  isOpen: boolean;
  isEdit?: boolean;
  onOpenChange: (open: boolean) => void;
  formData: QuestionFormData;
  onFormDataChange: React.Dispatch<React.SetStateAction<QuestionFormData>>;
  subjects: Subject[];
  topics: Topic[];
  onSubjectChange: (subjectId: string) => void;
  onTypeChange: (type: QuestionType) => void;
  onSubmit: () => Promise<void>;
}

const NONE = '__none__';

export function QuestionDialog({
  isDark,
  isOpen,
  isEdit,
  onOpenChange,
  formData,
  onFormDataChange,
  subjects,
  topics,
  onSubjectChange,
  onTypeChange,
  onSubmit,
}: QuestionDialogProps) {
  const inputCls = cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200');
  const labelCls = cn(isDark ? 'text-slate-300' : 'text-slate-700');

  const updateOption = (index: number, patch: Partial<CreateOptionDto>) => {
    onFormDataChange((prev) => {
      const opts = [...(prev.options || [])];
      opts[index] = { ...opts[index], ...patch };
      return { ...prev, options: opts };
    });
  };

  const addOption = () => {
    onFormDataChange((prev) => {
      const opts = [...(prev.options || [])];
      const nextLabel = String.fromCharCode(65 + opts.length);
      opts.push({ label: nextLabel, text: '', is_correct: false, sort_order: opts.length });
      return { ...prev, options: opts };
    });
  };

  const removeOption = (index: number) => {
    onFormDataChange((prev) => {
      const opts = [...(prev.options || [])];
      opts.splice(index, 1);
      return { ...prev, options: opts };
    });
  };

  const updateSubQuestion = (sqIndex: number, patch: Partial<CreateSubQuestionDto>) => {
    onFormDataChange((prev) => {
      const sqs = [...(prev.sub_questions || [])];
      sqs[sqIndex] = { ...sqs[sqIndex], ...patch };
      return { ...prev, sub_questions: sqs };
    });
  };

  const addSubQuestion = () => {
    onFormDataChange((prev) => {
      const sqs = [...(prev.sub_questions || [])];
      sqs.push({
        text: '',
        sort_order: sqs.length,
        sub_options: [
          { label: 'A', text: '', is_correct: false, sort_order: 0 },
          { label: 'B', text: '', is_correct: false, sort_order: 1 },
        ],
      });
      return { ...prev, sub_questions: sqs };
    });
  };

  const removeSubQuestion = (sqIndex: number) => {
    onFormDataChange((prev) => {
      const sqs = [...(prev.sub_questions || [])];
      sqs.splice(sqIndex, 1);
      return { ...prev, sub_questions: sqs };
    });
  };

  const updateSubOption = (sqIndex: number, soIndex: number, patch: Partial<CreateOptionDto>) => {
    onFormDataChange((prev) => {
      const sqs = [...(prev.sub_questions || [])];
      const opts = [...(sqs[sqIndex]?.sub_options || [])];
      opts[soIndex] = { ...opts[soIndex], ...patch };
      sqs[sqIndex] = { ...sqs[sqIndex], sub_options: opts };
      return { ...prev, sub_questions: sqs };
    });
  };

  const addSubOption = (sqIndex: number) => {
    onFormDataChange((prev) => {
      const sqs = [...(prev.sub_questions || [])];
      const opts = [...(sqs[sqIndex]?.sub_options || [])];
      const nextLabel = String.fromCharCode(65 + opts.length);
      opts.push({ label: nextLabel, text: '', is_correct: false, sort_order: opts.length });
      sqs[sqIndex] = { ...sqs[sqIndex], sub_options: opts };
      return { ...prev, sub_questions: sqs };
    });
  };

  const removeSubOption = (sqIndex: number, soIndex: number) => {
    onFormDataChange((prev) => {
      const sqs = [...(prev.sub_questions || [])];
      const opts = [...(sqs[sqIndex]?.sub_options || [])];
      opts.splice(soIndex, 1);
      sqs[sqIndex] = { ...sqs[sqIndex], sub_options: opts };
      return { ...prev, sub_questions: sqs };
    });
  };

  const showSubs = formData.type === 'complex_with_subs' || formData.type === 'exclusive_subs';
  const showInput = formData.type === 'complex_with_input';

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'max-w-4xl max-h-[90vh] overflow-y-auto',
          isDark ? 'bg-[#1E293B] border-slate-700' : 'bg-white',
        )}
      >
        <DialogHeader>
          <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#0F172A]')}>
            {isEdit ? 'Edit question' : 'Create question'}
          </DialogTitle>
          <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
            Configure the question, options, and (if applicable) sub-questions.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Subject + Topic + Type */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className={labelCls}>Subject *</Label>
              <Select
                value={formData.subject_id || ''}
                onValueChange={(v) => {
                  onFormDataChange((prev) => ({ ...prev, subject_id: v, topic_id: null }));
                  onSubjectChange(v);
                }}
                disabled={isEdit}
              >
                <SelectTrigger className={inputCls}>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {subjects.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Topic</Label>
              <Select
                value={formData.topic_id || NONE}
                onValueChange={(v) =>
                  onFormDataChange((prev) => ({ ...prev, topic_id: v === NONE ? null : v }))
                }
                disabled={!formData.subject_id}
              >
                <SelectTrigger className={inputCls}>
                  <SelectValue placeholder="No topic" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>No topic</SelectItem>
                  {topics.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Type</Label>
              <Select value={formData.type || 'simple'} onValueChange={(v) => onTypeChange(v as QuestionType)}>
                <SelectTrigger className={inputCls}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="simple">Simple</SelectItem>
                  <SelectItem value="complex_with_subs">Complex (sub-questions)</SelectItem>
                  <SelectItem value="exclusive_subs">Exclusive subs</SelectItem>
                  <SelectItem value="complex_with_input">Complex with input</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={labelCls}>Difficulty</Label>
              <Select
                value={formData.difficulty || NONE}
                onValueChange={(v) =>
                  onFormDataChange((prev) => ({
                    ...prev,
                    difficulty: v === NONE ? null : (v as any),
                  }))
                }
              >
                <SelectTrigger className={inputCls}>
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>—</SelectItem>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Image URL</Label>
              <Input
                value={formData.image || ''}
                onChange={(e) => onFormDataChange((prev) => ({ ...prev, image: e.target.value || null }))}
                className={inputCls}
              />
            </div>
          </div>

          {/* Question text */}
          <div className="space-y-2">
            <Label className={labelCls}>Question text *</Label>
            <Textarea
              value={formData.text}
              onChange={(e) => onFormDataChange((prev) => ({ ...prev, text: e.target.value }))}
              rows={3}
              className={inputCls}
            />
          </div>

          {/* Explanation */}
          <div className="space-y-2">
            <Label className={labelCls}>Explanation</Label>
            <Textarea
              value={formData.explanation || ''}
              onChange={(e) =>
                onFormDataChange((prev) => ({ ...prev, explanation: e.target.value || null }))
              }
              rows={2}
              className={inputCls}
            />
          </div>

          {/* Text answer for input type */}
          {showInput && (
            <div className="space-y-2">
              <Label className={labelCls}>Correct text answer</Label>
              <Input
                value={formData.correct_text_answer || ''}
                onChange={(e) =>
                  onFormDataChange((prev) => ({ ...prev, correct_text_answer: e.target.value || null }))
                }
                className={inputCls}
              />
            </div>
          )}

          {/* Options editor */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className={labelCls}>Options</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={addOption}
                className="h-7 text-xs text-[#0EA5A4]"
              >
                <Plus className="w-3 h-3 mr-1" /> Add option
              </Button>
            </div>
            <div className="space-y-2">
              {(formData.options || []).map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    value={opt.label}
                    onChange={(e) => updateOption(i, { label: e.target.value })}
                    className={cn('w-14', inputCls)}
                  />
                  <Input
                    value={opt.text}
                    onChange={(e) => updateOption(i, { text: e.target.value })}
                    placeholder="Option text"
                    className={cn('flex-1', inputCls)}
                  />
                  <label className="flex items-center gap-1 text-xs text-slate-500 whitespace-nowrap">
                    <Checkbox
                      checked={opt.is_correct}
                      onCheckedChange={(v) => updateOption(i, { is_correct: !!v })}
                    />
                    correct
                  </label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeOption(i)}
                    className="h-8 px-2 text-slate-500 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Sub-questions editor */}
          {showSubs && (
            <div className="space-y-3 pt-2 border-t border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <Label className={labelCls}>Sub-questions</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={addSubQuestion}
                  className="h-7 text-xs text-[#0EA5A4]"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add sub-question
                </Button>
              </div>

              {(formData.sub_questions || []).map((sq, sqi) => (
                <div
                  key={sqi}
                  className={cn(
                    'p-3 rounded-lg border space-y-2',
                    isDark ? 'border-slate-700 bg-slate-800/50' : 'border-slate-200 bg-slate-50',
                  )}
                >
                  <div className="flex items-start gap-2">
                    <Textarea
                      value={sq.text}
                      onChange={(e) => updateSubQuestion(sqi, { text: e.target.value })}
                      placeholder={`Sub-question #${sqi + 1}`}
                      rows={2}
                      className={cn('flex-1', inputCls)}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeSubQuestion(sqi)}
                      className="h-8 px-2 text-slate-500 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="space-y-1.5 pl-2">
                    {(sq.sub_options || []).map((so, soi) => (
                      <div key={soi} className="flex items-center gap-2">
                        <Input
                          value={so.label}
                          onChange={(e) => updateSubOption(sqi, soi, { label: e.target.value })}
                          className={cn('w-12', inputCls)}
                        />
                        <Input
                          value={so.text}
                          onChange={(e) => updateSubOption(sqi, soi, { text: e.target.value })}
                          placeholder="Sub-option text"
                          className={cn('flex-1', inputCls)}
                        />
                        <label className="flex items-center gap-1 text-xs text-slate-500 whitespace-nowrap">
                          <Checkbox
                            checked={so.is_correct}
                            onCheckedChange={(v) => updateSubOption(sqi, soi, { is_correct: !!v })}
                          />
                          correct
                        </label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeSubOption(sqi, soi)}
                          className="h-7 px-2 text-slate-500 hover:text-red-500"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => addSubOption(sqi)}
                      className="h-7 text-xs text-[#0EA5A4]"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add sub-option
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className={cn(isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200')}
          >
            Cancel
          </Button>
          <Button onClick={onSubmit} className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90">
            {isEdit ? 'Save changes' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
